import React, { useMemo, useState } from 'react';
import type { DailyRecord } from '@skd/shared';
import { INSPECTION_STAGES, INSPECTION_ITEMS, isItemApplicable } from '@skd/shared';
import { Button } from '../ui/Button';
import { StatusChip } from '../ui/StatusChip';

interface SectionsDashboardProps {
  records: DailyRecord[];
}

function formatDateForInput(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function parseDateInput(value: string): Date | null {
  if (!value) return null;
  const parts = value.split('-');
  if (parts.length !== 3) return null;
  return new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
}

function parseRecordDate(record: DailyRecord & { updatedAt?: string; createdAt?: string }): Date | null {
  const ts = (record as any).updatedAt || (record as any).createdAt || record.timestamp;
  if (!ts) return null;
  const d = new Date(ts);
  return isNaN(d.getTime()) ? null : d;
}

function escapeCsv(value: string): string {
  if (value.includes(',') || value.includes('"') || value.includes('\n')) {
    return `"${value.replace(/"/g, '""')}"`;
  }
  return value;
}

export function SectionsDashboard({ records }: SectionsDashboardProps) {
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const [dateFrom, setDateFrom] = useState(formatDateForInput(thirtyDaysAgo));
  const [dateTo, setDateTo] = useState(formatDateForInput(today));
  const [openSections, setOpenSections] = useState<number[]>([]);

  const toggleSection = (id: number) =>
    setOpenSections((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));

  const fromDate = parseDateInput(dateFrom);
  const toDate = parseDateInput(dateTo);

  const filteredRecords = useMemo(() => {
    return records.filter((r) => {
      const d = parseRecordDate(r);
      if (!d) return true;
      if (fromDate && d < fromDate) return false;
      if (toDate) {
        const endOfDay = new Date(toDate);
        endOfDay.setHours(23, 59, 59, 999);
        if (d > endOfDay) return false;
      }
      return true;
    });
  }, [records, fromDate, toDate]);

  const sectionStats = useMemo(() => {
    const stats: Record<number, {
      totalChecked: number;
      passCount: number;
      defectCount: number;
      passRate: number;
      items: Record<string, { ok: number; ng: number; na: number }>;
    }> = {};

    for (const stage of INSPECTION_STAGES) {
      stats[stage.id] = {
        totalChecked: 0,
        passCount: 0,
        defectCount: 0,
        passRate: 0,
        items: {},
      };

      for (const item of INSPECTION_ITEMS) {
        stats[stage.id].items[item.id] = { ok: 0, ng: 0, na: 0 };
      }
    }

    for (const record of filteredRecords) {
      for (const stage of INSPECTION_STAGES) {
        const stageData = record.stages[stage.id];
        if (!stageData) continue;

        for (const item of INSPECTION_ITEMS) {
          if (!isItemApplicable(item.id, stage.id)) continue;
          const itemData = stageData.items[item.id];
          if (!itemData) continue;

          stats[stage.id].totalChecked++;
          if (itemData.status === 'OK') {
            stats[stage.id].passCount++;
            stats[stage.id].items[item.id].ok++;
          } else if (itemData.status === 'NG') {
            stats[stage.id].defectCount++;
            stats[stage.id].items[item.id].ng++;
          } else {
            stats[stage.id].items[item.id].na++;
          }
        }

        const s = stats[stage.id];
        s.passRate = s.totalChecked > 0 ? Math.round((s.passCount / s.totalChecked) * 100) : 0;
      }
    }

    return stats;
  }, [filteredRecords]);

  const handleExportExcel = () => {
    const rows: string[] = [];

    rows.push(`SKD Body Quality Control Portal - 5-Section Dashboard Export`);
    rows.push(`Date Range: ${dateFrom || 'any'} to ${dateTo || 'any'}`);
    rows.push(`Generated: ${new Date().toLocaleString()}`);
    rows.push(`Total Bodies: ${filteredRecords.length}`);
    rows.push('');

    rows.push('SECTION,BODY NO,MODEL,INVOICE,ITEM,STATUS,DEFECT TYPE,REMARK,INSPECTOR,DATE');

    for (const record of filteredRecords) {
      for (const stage of INSPECTION_STAGES) {
        const stageData = record.stages[stage.id];
        if (!stageData) continue;

        for (const item of INSPECTION_ITEMS) {
          if (!isItemApplicable(item.id, stage.id)) continue;
          const itemData = stageData.items[item.id];
          if (!itemData) continue;

          rows.push([
            escapeCsv(stage.shortName),
            escapeCsv(record.metadata.body_number),
            escapeCsv(record.metadata.model),
            escapeCsv(record.metadata.invoice_no),
            escapeCsv(`${item.code} - ${item.label}`),
            escapeCsv(itemData.status),
            escapeCsv(itemData.defectType || ''),
            escapeCsv(itemData.remark || ''),
            escapeCsv(stageData.inspectorFure || ''),
            escapeCsv(stageData.dateTime || ''),
          ].join(','));
        }
      }
    }

    const bom = '\uFEFF';
    const blob = new Blob([bom + rows.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `skd-sections-dashboard-${dateFrom || 'all'}-to-${dateTo || 'all'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  /** Per-item breakdown, shared by the phone accordion and the desktop columns. */
  const itemBreakdown = (stageId: number) => (
    <ul className="flex flex-col">
      {INSPECTION_ITEMS.filter((item) => isItemApplicable(item.id, stageId)).map((item) => {
        const itemStats = sectionStats[stageId].items[item.id];
        const hasNG = itemStats.ng > 0;
        return (
          <li
            key={item.id}
            className="flex items-center justify-between gap-2 border-b border-[var(--border-color)] py-1.5 text-[0.75rem] last:border-b-0"
          >
            <span className="flex min-w-0 items-baseline gap-1.5">
              <span className="text-[var(--text-dim)]">({item.id})</span>
              <span className="truncate text-[var(--text-muted)]">{item.code}</span>
            </span>
            {hasNG ? (
              <StatusChip status="ng">{itemStats.ng} NG</StatusChip>
            ) : (
              <span className="tnum text-[var(--text-dim)]">{itemStats.ok} OK</span>
            )}
          </li>
        );
      })}
    </ul>
  );

  return (
    <div className="flex flex-col gap-4 sm:gap-5">
      {/* One filter row above everything it scopes. */}
      <div className="flex flex-col gap-3 rounded-[var(--radius-lg)] border border-[var(--card-border)] bg-[var(--card-bg)] p-3 sm:flex-row sm:items-end sm:justify-between sm:p-4">
        <div className="grid grid-cols-2 gap-3 sm:flex sm:items-end">
          <div className="form-group">
            <label className="form-label" htmlFor="range-from">From</label>
            <input
              id="range-from"
              type="date"
              className="input-field sm:w-40"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
            />
          </div>
          <div className="form-group">
            <label className="form-label" htmlFor="range-to">To</label>
            <input
              id="range-to"
              type="date"
              className="input-field sm:w-40"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
            />
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 sm:justify-end">
          <span className="text-[0.75rem] text-[var(--text-dim)]">
            {filteredRecords.length} of {records.length} bodies
          </span>
          <Button variant="secondary" onClick={handleExportExcel}>
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
            Export CSV
          </Button>
        </div>
      </div>

      {/* Phone: one collapsible row per section. */}
      <div className="card divide-y divide-[var(--border-color)] overflow-hidden md:hidden">
        {INSPECTION_STAGES.map((stage) => {
          const stats = sectionStats[stage.id];
          const isOpen = openSections.includes(stage.id);
          return (
            <div key={stage.id}>
              <button
                type="button"
                onClick={() => toggleSection(stage.id)}
                aria-expanded={isOpen}
                className="flex w-full items-center gap-3 p-4 text-left"
              >
                <svg
                  className={`flex-shrink-0 text-[var(--text-dim)] transition-transform ${isOpen ? 'rotate-90' : ''}`}
                  width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                >
                  <polyline points="9 18 15 12 9 6" />
                </svg>
                <span className="min-w-0 flex-1">
                  <span className="block text-[0.875rem] font-medium text-[var(--text-main)]">
                    {stage.shortName}
                  </span>
                  <span className="mt-0.5 block text-[0.75rem] text-[var(--text-dim)]">
                    <span className="tnum">{stats.totalChecked}</span> items ·{' '}
                    <span className="tnum">{stats.passRate}%</span> pass
                  </span>
                </span>
                {stats.defectCount === 0 ? (
                  <StatusChip status="ok">Clean</StatusChip>
                ) : (
                  <StatusChip status="ng">{stats.defectCount} NG</StatusChip>
                )}
              </button>
              {isOpen && <div className="px-4 pb-4">{itemBreakdown(stage.id)}</div>}
            </div>
          );
        })}
      </div>

      {/* Desktop: five columns, flattened — no boxes inside boxes. */}
      <div className="hidden gap-4 md:grid md:grid-cols-2 xl:grid-cols-5">
        {INSPECTION_STAGES.map((stage) => {
          const stats = sectionStats[stage.id];
          return (
            <section key={stage.id} className="card flex flex-col p-4">
              <div className="flex items-start justify-between gap-2">
                <h3 className="card-title">{stage.shortName}</h3>
                {stats.defectCount === 0 ? (
                  <StatusChip status="ok">Clean</StatusChip>
                ) : (
                  <StatusChip status="ng">{stats.defectCount}</StatusChip>
                )}
              </div>

              <dl className="mt-3 flex items-baseline gap-4 border-b border-[var(--border-color)] pb-3 text-[0.75rem]">
                <div>
                  <dt className="text-[var(--text-dim)]">Items</dt>
                  <dd className="mt-0.5 text-[1rem] font-semibold tnum text-[var(--text-main)]">{stats.totalChecked}</dd>
                </div>
                <div>
                  <dt className="text-[var(--text-dim)]">Pass</dt>
                  <dd className="mt-0.5 text-[1rem] font-semibold tnum text-[var(--text-main)]">{stats.passRate}%</dd>
                </div>
              </dl>

              <div className="mt-2">{itemBreakdown(stage.id)}</div>
            </section>
          );
        })}
      </div>

      {/* Summary table */}
      <section className="card overflow-hidden">
        <div className="border-b border-[var(--border-color)] p-4">
          <h2 className="card-title">OK / NG breakdown by section</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Section</th>
                <th className="text-right">Checked</th>
                <th className="text-right">OK</th>
                <th className="text-right">NG</th>
                <th>Pass rate</th>
              </tr>
            </thead>
            <tbody>
              {INSPECTION_STAGES.map((stage) => {
                const stats = sectionStats[stage.id];
                return (
                  <tr key={stage.id}>
                    <td className="font-medium">{stage.shortName}</td>
                    <td className="text-right tnum">{stats.totalChecked}</td>
                    <td className="text-right tnum">{stats.passCount}</td>
                    <td className="text-right tnum">{stats.defectCount}</td>
                    <td>
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-full max-w-[120px] overflow-hidden rounded-full bg-[var(--viz-track)]">
                          <div
                            className="h-full rounded-full"
                            style={{ width: `${stats.passRate}%`, background: 'var(--viz-series-1)' }}
                          />
                        </div>
                        <span className="tnum text-[0.75rem] text-[var(--text-muted)]">{stats.passRate}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
